import { useEffect, useId, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { hasWorkCabinets, showAdministrator, showOrganizer, type CabinetId, type RoleCabinetId } from "./cabinetNav"
import type { CabinetCounters } from "./useCabinetCounters"
import { useCabinetEntries } from "./useCabinetEntries"

const PendingBadge = ({ count, active }: { count: number | undefined; active: boolean }) => {
  const { t } = useTranslation()
  if (!count) return null
  return (
    <span
      className={`min-w-5 h-5 px-1.5 rounded-full text-[11px] leading-5 text-center ${active ? "bg-white/25 text-white" : "bg-[var(--ws-accent)] text-white"}`}
      aria-label={t("workspaces.pendingBadge", { count })}
    >
      {count > 99 ? "99+" : count}
    </span>
  )
}

export const CabinetSwitcher = ({
  current,
  available,
  counters,
  canCreateEvents,
  canModerateEvents,
  isAdmin,
}: {
  current: CabinetId | null
  available: RoleCabinetId[]
  counters: CabinetCounters
  canCreateEvents: boolean
  canModerateEvents: boolean
  isAdmin: boolean
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const access = { canCreateEvents, canModerateEvents, isAdmin }
  const entries = useCabinetEntries(available, access).filter((entry) => entry.id !== "summary")
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const listId = useId()
  const currentLabel = t(current ? `workspaces.${current}` : "workspaces.summary")

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return
      if (rootRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener("mousedown", onPointer)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onPointer)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  if (!hasWorkCabinets(available, showOrganizer(access) || showAdministrator(access))) return null

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        ref={buttonRef}
        type="button"
        className="cabinet-script inline-flex items-center gap-2 max-w-[14rem] rounded-full border border-white/70 bg-transparent px-3 py-1 text-lg leading-none text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${t("workspaces.switcher")}: ${currentLabel}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="truncate">{currentLabel}</span>
        <span
          aria-hidden
          className={`inline-block h-1.5 w-1.5 border-b border-r border-current ${open ? "mt-0.5 -rotate-[135deg]" : "-mt-0.5 rotate-45"}`}
        />
      </button>
      {open ? (
        <nav
          id={listId}
          aria-label={t("workspaces.switcher")}
          className="cabinet-switch-panel absolute left-0 top-full z-50 mt-2 w-80 max-w-[80vw] rounded-2xl border border-lib-border bg-lib-card p-1.5 text-lib-text shadow-lg"
        >
          {entries.map((entry) => {
            const activeRole = entry.id === current
            const hintId = `${listId}-${entry.id}`
            return (
              <Link
                key={entry.id}
                to={entry.to}
                data-workspace={entry.id}
                aria-current={activeRole ? "page" : undefined}
                aria-label={entry.label}
                aria-describedby={entry.id === "summary" ? undefined : hintId}
                onClick={() => setOpen(false)}
                className={`workspace-shell flex items-center gap-2.5 rounded-lg px-2.5 py-2 ${
                  activeRole ? "bg-[var(--ws-accent)] text-white" : "text-lib-text hover:bg-lib-bg"
                }`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${activeRole ? "bg-white" : "bg-[var(--ws-accent)]"}`} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="cabinet-script block truncate text-lg leading-tight">{entry.label}</span>
                  {entry.id === "summary" ? null : (
                    <span id={hintId} className={`block text-xs leading-snug ${activeRole ? "text-white" : "text-lib-text-secondary"}`}>
                      {t(`workspaces.hints.${entry.id}`)}
                    </span>
                  )}
                </span>
                {entry.id !== "reader" ? <PendingBadge count={counters[entry.id]} active={activeRole} /> : null}
              </Link>
            )
          })}
        </nav>
      ) : null}
    </div>
  )
}
